import React, { useState } from 'react';
import { useTranslation } from 'react-i18next'; // ✅ i18n
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faLock, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from './apiConfig';

const AddReview = ({ subserviceId, onReviewSuccess }) => {
    const { t } = useTranslation(); // ✅ i18n hook
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);

    const goldColor = '#c5a059';

    const savedUser = localStorage.getItem('liber_user');
    const token = localStorage.getItem('token');
    const user = savedUser ? JSON.parse(savedUser) : null;

    if (!user) {
        return (
            <div style={lockContainerStyle}>
                <div style={lockCircleStyle}>
                    <FontAwesomeIcon icon={faLock} style={{ fontSize: '24px', color: '#64748b' }} />
                </div>
                <h4 style={{ margin: '10px 0 5px', color: '#0f172a' }}>{t('review_access_denied')}</h4>
                <p style={{ margin: 0, fontSize: '14px', color: '#94a3b8' }}>{t('review_login_prompt')}</p>
            </div>
        );
    }

    const handleSubmit = async () => {
        if (rating === 0) return alert(t('alert_select_stars'));
        if (!comment.trim()) return alert(t('alert_write_review'));
        if (!token) return alert(t('alert_relogin'));

        setLoading(true);

        const reviewData = {
            subservice_id: Number(subserviceId),
            service_id: 0,
            name: user.full_name || user.name || t('customer_default_name'),
            text: comment.trim(),
            stars: rating
        };

        try {
            const res = await fetch(`${API_BASE}/api/reviews`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'ngrok-skip-browser-warning': 'true',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(reviewData)
            });

            const data = await res.json();

            if (res.ok && data.success) {
                setRating(0);
                setComment('');
                alert(t('review_success_msg'));
                if (onReviewSuccess) onReviewSuccess();
            } else {
                alert(data.message || t('review_error_msg'));
            }
        } catch (err) {
            console.error("Review error:", err);
            alert(t('error_server_connection'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={formStyle}>
            <div style={headerStyle}>
                <h4 style={titleStyle}>{t('review_form_title')}</h4>
                <p style={subtitleStyle}>{t('review_form_subtitle')}</p>
            </div>
            
            <div style={starsContainerStyle}>
                {[1, 2, 3, 4, 5].map((star) => (
                    <FontAwesomeIcon
                        key={star}
                        icon={faStar}
                        onMouseEnter={() => setHover(star)}
                        onMouseLeave={() => setHover(0)}
                        onClick={() => setRating(star)}
                        style={{
                            fontSize: '32px',
                            cursor: 'pointer',
                            color: star <= (hover || rating) ? goldColor : '#e2e8f0',
                            transform: star <= (hover || rating) ? 'scale(1.1)' : 'scale(1)',
                            transition: 'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                        }}
                    />
                ))}
            </div>

            <div style={inputWrapperStyle}>
                <textarea
                    placeholder={t('placeholder_review_text')}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    style={textareaStyle}
                />
            </div>

            <button 
                onClick={handleSubmit} 
                disabled={loading} 
                style={loading ? { ...btnStyle, ...btnDisabledStyle } : btnStyle}
            >
                {loading ? t('btn_sending') : (
                    <>
                        <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: '10px' }} />
                        {t('btn_send_review')}
                    </>
                )}
            </button>
        </div>
    );
};


const formStyle = { 
    background: '#ffffff', 
    padding: '30px', 
    borderRadius: '24px', 
    border: '1px solid #f1f5f9', 
    boxShadow: '0 15px 35px rgba(0,0,0,0.05)', 
    marginBottom: '40px',
    maxWidth: '100%',
    boxSizing: 'border-box'
};

const headerStyle = { marginBottom: '25px' };
const titleStyle = { margin: '0 0 5px 0', fontSize: '20px', fontWeight: '800', color: '#0f172a' };
const subtitleStyle = { margin: 0, fontSize: '14px', color: '#64748b' };

const starsContainerStyle = { 
    marginBottom: '25px', 
    display: 'flex', 
    gap: '8px',
    justifyContent: 'flex-start'
};

const inputWrapperStyle = { position: 'relative', marginBottom: '20px' };

const textareaStyle = { 
    width: '100%', 
    height: '130px', 
    padding: '18px', 
    borderRadius: '16px', 
    border: '2px solid #f1f5f9', 
    backgroundColor: '#f8fafc',
    outline: 'none', 
    fontSize: '15px',
    lineHeight: '1.6',
    resize: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.3s ease',
    color: '#1e293b',
    fontFamily: 'inherit'
};

const btnStyle = { 
    background: '#0f172a', 
    color: '#fff', 
    border: 'none', 
    padding: '16px 28px', 
    borderRadius: '14px', 
    cursor: 'pointer', 
    fontWeight: '700',
    width: '100%',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 10px 20px rgba(15, 23, 42, 0.15)',
    transition: 'all 0.3s ease'
};

const btnDisabledStyle = { opacity: 0.6, cursor: 'not-allowed', boxShadow: 'none' };

const lockContainerStyle = { 
    padding: '40px 20px', 
    background: '#f8fafc', 
    borderRadius: '24px', 
    textAlign: 'center', 
    border: '2px dashed #e2e8f0', 
    marginBottom: '40px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
};

const lockCircleStyle = {
    width: '60px',
    height: '60px',
    background: '#fff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '15px',
    boxShadow: '0 10px 20px rgba(0,0,0,0.03)'
};

export default AddReview;